import { endOfMonth, isSameDay, startOfMonth } from "date-fns";

import type { UserAvailableLocationType } from "~/modules/location/api/location/locationTypes.ts";
import type {
	QuotaConsumerForTracking,
	StaffingTimeTrackingSummary,
	StaffingWithRelations,
	UsersActiveStaffingType,
} from "~/modules/project/api/staffing/staffingTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";
import type { Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import type { UserAvailableDeliverableType } from "~/modules/timeTracking/api/deliverable/deliverableTypes.ts";
import type {
	TimeTrackingExtendedType,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import type { TimeTrackingTotalsByType } from "~/modules/timeTracking/types/timeTrackingTypes.ts";
import { formatDateToYYYYMMDD, isDateBetween } from "~/utils/dateAndTimeUtils.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

export function calculateTimeTrackingTotals({ endDate, startDate, timeTrackingData }: {
	endDate: Date,
	startDate: Date,
	timeTrackingData: TimeTrackingExtendedType[]
}): TimeTrackingTotalsByType {
	const totals: TimeTrackingTotalsByType = {
		[TimeTrackingTypeId.Internal]: 0,
		[TimeTrackingTypeId.Project]: 0,
		[TimeTrackingTypeId.Sales]: 0,
		[TimeTrackingTypeId.PartTime]: 0,
	};

	for (const timeTracking of timeTrackingData) {
		const timeTrackingDate = new Date(timeTracking.date);
		if (isDateBetween(timeTrackingDate, startDate, endDate)) {
			totals[timeTracking.timeTrackingTypeId as TimeTrackingTypeId] += timeTracking.minutes;
		}
	}

	return totals;
}

export function minutesToWorkdays(minutes: number, precision: number = 0): number {
	const result = minutes / 60 / 8;
	return Math.round(result * Math.pow(10, precision)) / Math.pow(10, precision);
}

export function formatManDays(manDays: number): string {
	return manDays.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function getStartAndEndOfMonthFormatted(date: Date): { startDate: string, endDate: string } {
	const dates = getStartAndEndOfMonthDates(date);

	return {
		startDate: formatDateToYYYYMMDD(dates.startDate),
		endDate: formatDateToYYYYMMDD(dates.endDate),
	};
}

export function getStartAndEndOfMonthDates(date: Date): { startDate: Date, endDate: Date } {
	return {
		startDate: startOfMonth(date),
		endDate: endOfMonth(date),
	};
}

export function getStaffingTimeTrackingSummaryFromActiveStaffings(staffingId: string,
	projects: UsersActiveStaffingType[]): StaffingTimeTrackingSummary & {
	projectTitle: string,
	phaseTitle: string,
	orderTitle: string
} | null {
	for (const project of projects) {
		for (const phase of project.projectPhases) {
			for (const order of phase.orders) {
				for (const staffing of order.staffings) {
					if (staffing.id === staffingId) {
						return {
							...staffing,
							projectTitle: project.title,
							phaseTitle: phase.title,
							orderTitle: order.title,
						};
					}
				}
			}
		}
	}
	return null;
}

// The staffing period includes both boundary days. A null start or end date is an open end.
export function isDateInsideStaffingPeriod(date: Date,
	{ startDate, endDate }: { startDate: string | null, endDate: string | null }): boolean {
	const day = formatDateToYYYYMMDD(date);

	return (!startDate || startDate <= day) && (!endDate || day <= endDate);
}

// The active staffings whose period does not contain the date. The backend refuses a tracking on them.
// `exceptStaffingId` is the stored staffing of an updated tracking: a tracking that keeps its staffing stays
// editable, even when it lies outside the period.
export function getStaffingIdsOutsidePeriod({ date, exceptStaffingId = null, projects }: {
	date: Date | null,
	exceptStaffingId?: string | null,
	projects: UsersActiveStaffingType[],
}): string[] {
	if (!date) {
		return [];
	}

	return projects
		.flatMap((project) => project.projectPhases)
		.flatMap((phase) => phase.orders)
		.flatMap((order) => order.staffings)
		.filter((staffing) => staffing.id !== exceptStaffingId && !isDateInsideStaffingPeriod(date, staffing))
		.map((staffing) => staffing.id);
}

// The projects without the given staffings. An order, a phase or a project with no staffing left goes too.
export function removeStaffingsFromProjects(projects: UsersActiveStaffingType[], staffingIds: string[]): UsersActiveStaffingType[] {
	if (staffingIds.length === 0) {
		return projects;
	}

	return projects
		.map((project) => ({
			...project,
			projectPhases: project.projectPhases
				.map((phase) => ({
					...phase,
					orders: phase.orders
						.map((order) => ({
							...order,
							staffings: order.staffings.filter((staffing) => !staffingIds.includes(staffing.id)),
						}))
						.filter((order) => order.staffings.length > 0),
				}))
				.filter((phase) => phase.orders.length > 0),
		}))
		.filter((project) => project.projectPhases.length > 0);
}

// The staffings with a project tracking on the given day. A staffing can have more than one tracking on a day.
export function getTrackedStaffingIdsOnDay({ date, exceptTimeTrackingId = null, timeTrackings }: {
	date: Date | null,
	exceptTimeTrackingId?: string | null,
	timeTrackings: TimeTrackingExtendedType[],
}): string[] {
	if (!date) {
		return [];
	}

	const staffingIds = timeTrackings
		.filter((timeTracking) => timeTracking.id !== exceptTimeTrackingId
			&& timeTracking.timeTrackingTypeId === TimeTrackingTypeId.Project
			&& isSameDay(new Date(timeTracking.date), date))
		.flatMap((timeTracking) => timeTracking.staffingId ?? []);

	return [...new Set(staffingIds)];
}

export function getStaffingTimeTrackingSummaryFromStaffing(staffing: StaffingWithRelations): StaffingTimeTrackingSummary {
	return {
		id: staffing.id,
		clientId: staffing.project.clientId,
		isActive: staffing.isActive,
		isBillable: staffing.isBillable,
		manDaysPlanned: staffing.manDays,
		minutesTracked: staffing.minutesTracked,
		orderId: staffing.orderId,
		orderTitle: staffing.order.title,
		phaseTitle: getFullPhaseTitle(staffing.projectPhase),
		projectId: staffing.projectId,
		projectPhaseId: staffing.projectPhaseId,
		projectRoleId: staffing.projectRoleId,
		projectTitle: staffing.project.title,
		dailyRateCents: staffing.dailyRateCents,
		// A staffing loaded on its own carries no consumers, so it offers no quota booking.
		quotaConsumers: [],
		userId: staffing.userId,
	};
}

export type QuotaBookingOption = {
	// The amount the tracking can book to the quota. On an update it includes the tracking's own current draw.
	availableCents: number;
	consumer: QuotaConsumerForTracking;
	quota: Quota;
};

// The quota booking a tracking holds before the update.
export type CurrentQuotaBooking = {
	minutes: number;
	quotaId: string;
	staffingId: string;
};

// The value of a booking, as the backend calculates it (TimeTrackingQuotaBookingRule).
export function getQuotaDrawCents(minutes: number, dailyRateCents: number): number {
	return Math.round(minutes / 480 * dailyRateCents);
}

// A consumer takes a booking on a day when time tracking is enabled, the quota is bookable, and the
// day is in the usable period. A null bound is open. The backend sends only quotas with an invoice.
function quotaTakesBookingOnDay({ consumer, day, quota }: {
	consumer: QuotaConsumerForTracking;
	day: string;
	quota: Quota;
}): boolean {
	const startsAfterDay = quota.usableFrom !== null && quota.usableFrom.slice(0, 10) > day;
	const endsBeforeDay = quota.usableUntil !== null && quota.usableUntil.slice(0, 10) < day;

	return consumer.timeTrackingEnabled && quota.isBookable && !startsAfterDay && !endsBeforeDay;
}

// The quotas a tracking on the given day can book to.
export function getQuotaBookingOptions({ currentQuotaBooking, date, quotas, staffing }: {
	currentQuotaBooking: CurrentQuotaBooking | null;
	date: Date | null;
	quotas: Record<string, Quota>;
	staffing: StaffingTimeTrackingSummary;
}): QuotaBookingOption[] {
	if (!date) {
		return [];
	}

	const day = formatDateToYYYYMMDD(date);

	return staffing.quotaConsumers.reduce<QuotaBookingOption[]>((options, consumer) => {
		const quota = quotas[consumer.quotaId];

		if (!quota || !quotaTakesBookingOnDay({ consumer, day, quota })) {
			return options;
		}

		// The backend frees the tracking's own draw only when the quota and the staffing stay the same.
		const holdsOwnDraw = currentQuotaBooking?.quotaId === quota.id && currentQuotaBooking.staffingId === staffing.id;
		const ownDrawCents = holdsOwnDraw ? getQuotaDrawCents(currentQuotaBooking.minutes, staffing.dailyRateCents) : 0;

		options.push({ availableCents: consumer.remainingCents + ownDrawCents, consumer, quota });

		return options;
	}, []);
}

export function getAvailableUserDeliverables({
	userDeliverables,
	selectedStaffingProjectId,
	selectedTimeTrackingTypeId,
}: {
	userDeliverables: UserAvailableDeliverableType[],
	selectedStaffingProjectId: string | null | undefined,
	selectedTimeTrackingTypeId: string
}): UserAvailableDeliverableType[] {
	let preFilteredAvailableDeliverables = userDeliverables.filter(
		(deliverable) => selectedTimeTrackingTypeId === deliverable.timeTrackingTypeId);

	if (selectedStaffingProjectId) {
		preFilteredAvailableDeliverables = preFilteredAvailableDeliverables.filter(l => l.projectId === selectedStaffingProjectId);
	}

	// Return single array with favorites first, then non-favorites
	return [
		...preFilteredAvailableDeliverables
			.filter(d => d.isFavorite)
			.sort(byObjectProperty("displayName")),
		...preFilteredAvailableDeliverables
			.filter(d => !d.isFavorite)
			.sort(byObjectProperty("displayName")),
	];
}

export function getAvailableUserLocations({
	userLocations,
	selectedStaffingProjectId,
	selectedTimeTrackingTypeId,
}: {
	userLocations: UserAvailableLocationType[],
	selectedStaffingProjectId: string | null | undefined,
	selectedTimeTrackingTypeId: string
}): UserAvailableDeliverableType[] {
	let preFilteredLocations = userLocations.filter(
		(location) => selectedTimeTrackingTypeId === location.timeTrackingTypeId);

	if (selectedStaffingProjectId) {
		preFilteredLocations = preFilteredLocations.filter(location => location.projectId === selectedStaffingProjectId);
	}

	return [
		// Then favorites
		...preFilteredLocations
			.filter(location => location.isFavorite)
			.sort(byObjectProperty("displayName")),
		// Then non-favorites
		...preFilteredLocations
			.filter(location => !location.isFavorite)
			.sort(byObjectProperty("displayName")),

	];
}
